import { useMemo, useState } from 'react';
import { Calendar as CalendarIcon } from 'lucide-react';
import { DateCalendar } from '@/components/common/DateCalendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { CALENDAR_POPOVER_CLASS } from '@/styles/floatingPanel';
import { formatDate, formatDisplayDate } from '@/utils/date';

interface DatePickerInputProps {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  className?: string;
  maxDate?: string;
  'aria-invalid'?: boolean;
}

function parseLocalDate(dateStr: string): Date {
  const parts = dateStr.split('-');
  const year = Number(parts[0] ?? new Date().getFullYear());
  const month = Number(parts[1] ?? new Date().getMonth() + 1);
  const day = Number(parts[2] ?? new Date().getDate());
  return new Date(year, month - 1, day);
}

export function DatePickerInput({
  id,
  value,
  onChange,
  className,
  maxDate,
  'aria-invalid': ariaInvalid,
}: DatePickerInputProps) {
  const [open, setOpen] = useState(false);

  const selected = useMemo(() => (value ? parseLocalDate(value) : undefined), [value]);
  const disabled = useMemo(
    () => (maxDate ? { after: parseLocalDate(maxDate) } : undefined),
    [maxDate],
  );

  const formatted = selected ? formatDisplayDate(selected) : 'Pick a date';

  function handleSelect(date: Date | undefined) {
    if (date) {
      onChange(formatDate(date));
      setOpen(false);
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        id={id}
        aria-invalid={ariaInvalid}
        className={cn(
          'flex items-center gap-2 h-10 sm:h-9 rounded-md border bg-card px-3 cursor-pointer hover:bg-accent transition-colors select-none outline-none',
          'focus-visible:ring-1 focus-visible:ring-ring',
          'aria-invalid:border-destructive aria-invalid:ring-1 aria-invalid:ring-destructive/20',
          '',
          className,
        )}
      >
        <CalendarIcon className="size-4 text-muted-foreground shrink-0 md:hidden lg:block" />
        <span className="text-sm font-medium">{formatted}</span>
      </PopoverTrigger>
      <PopoverContent className={CALENDAR_POPOVER_CLASS} align="end">
        <DateCalendar
          mode="single"
          selected={selected}
          onSelect={handleSelect}
          {...(disabled && { disabled })}
          {...(selected && { defaultMonth: selected })}
        />
      </PopoverContent>
    </Popover>
  );
}
